import { useEffect, useState } from 'react';

import { Alert, Badge, Button, Card, Group, Loader, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconPlayerPlayFilled } from '@tabler/icons-react';

import { useAuth } from '@/contexts/AuthContext';
import { ApiConflictError, vnextApiClient } from '@/services/vnext-api';
import { Subject } from '@/types';
import { isClientAnalyst } from '@/types/auth';

interface PendingActivationProps {
  reportId: string;
  /** Called after a successful activation so the parent refetches into the
   * normal processing/running view. */
  onActivated: () => void;
}

// Keys on `additional_info` that are merge buckets rather than displayable
// facts — we skip them and show the flat, human-readable fields instead.
const HIDDEN_INFO_KEYS = new Set(['original', 'llm_findings', 'user_overrides', 'raw_query']);

// Clients get a deliberately basic pre-start view: identity facts only.
// Everything else the extraction gathered (addresses, employers, family,
// links…) stays admin-side until the report is delivered.
const BASIC_INFO_KEYS = ['date_of_birth', 'nationality', 'registration_number', 'imo_number'];

function humanizeKey(key: string): string {
  const spaced = key.replace(/_/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function displayableInfo(
  additionalInfo: Record<string, unknown> | null | undefined,
  basicOnly: boolean
) {
  const entries = Object.entries(additionalInfo ?? {}).filter(([key, value]) => {
    if (HIDDEN_INFO_KEYS.has(key)) {
      return false;
    }
    if (basicOnly && !BASIC_INFO_KEYS.includes(key)) {
      return false;
    }
    if (value === null || value === undefined || value === '') {
      return false;
    }
    // Keep primitives and arrays; skip nested objects (the merge buckets).
    return typeof value !== 'object' || Array.isArray(value);
  });
  if (basicOnly) {
    entries.sort((a, b) => BASIC_INFO_KEYS.indexOf(a[0]) - BASIC_INFO_KEYS.indexOf(b[0]));
  }
  return entries;
}

function SubjectDetailCard({
  subject,
  basicOnly,
}: Readonly<{ subject: Subject; basicOnly: boolean }>) {
  const info = displayableInfo(subject.additionalInfo, basicOnly);

  return (
    <Card withBorder padding="lg" radius="md">
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Title order={4}>{subject.subjectName}</Title>
          <Badge variant="light" color="gray">
            {subject.subjectType}
          </Badge>
        </Group>

        {subject.jurisdictions.length > 0 && (
          <Group gap="xs">
            {subject.jurisdictions.map((j) => (
              <Badge key={j.alpha2code} variant="outline" color="blue" size="sm">
                {j.name}
              </Badge>
            ))}
          </Group>
        )}

        {subject.reference && (
          <Text size="sm" c="dimmed">
            Reference: {subject.reference}
          </Text>
        )}

        {info.length > 0 && (
          <Stack gap={2}>
            {info.map(([key, value]) => (
              <Text key={key} size="sm">
                <Text component="span" c="dimmed">
                  {humanizeKey(key)}:
                </Text>{' '}
                {Array.isArray(value) ? value.join(', ') : String(value)}
              </Text>
            ))}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}

/**
 * Client/admin-facing view for a report sitting at `pending_activation`. Shows
 * ONLY the subject card(s) — no report outline/skeleton — plus a Start button
 * that activates the report (begins screening). Both clients (owning team) and
 * admins can activate; the backend enforces team membership (404 = not yours).
 */
export function PendingActivation({ reportId, onActivated }: Readonly<PendingActivationProps>) {
  const { user } = useAuth();
  const isAdmin = !!user?.isAdministrator;
  // Client analysts can review the pre-start view but starting
  // screening is an admin / project-manager decision (API 403s too).
  const canActivate = !isClientAnalyst(user);
  const [subjects, setSubjects] = useState<Subject[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [activating, setActivating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setSubjects(null);
    setLoadError(null);
    vnextApiClient
      .getReportSubjects(reportId)
      .then((result) => {
        if (!cancelled) {
          setSubjects(result);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : 'Failed to load subject details');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [reportId]);

  const handleActivate = async () => {
    setActivating(true);
    try {
      await vnextApiClient.activateReport(reportId);
      notifications.show({
        title: 'Screening started',
        message: 'Your report is now being generated.',
        color: 'green',
      });
      onActivated();
    } catch (err) {
      // 409 — already started elsewhere; just refresh into the running view.
      if (err instanceof ApiConflictError) {
        onActivated();
        return;
      }
      notifications.show({
        title: 'Could not start screening',
        message: err instanceof Error ? err.message : 'Please try again.',
        color: 'red',
      });
    } finally {
      setActivating(false);
    }
  };

  return (
    <Stack gap="md" maw={680} mx="auto" w="100%">
      <Stack gap={4}>
        <Title order={3}>Ready to start</Title>
        <Text size="sm" c="dimmed">
          Review the subject{(subjects?.length ?? 0) > 1 ? 's' : ''} below, then press Start to
          begin screening.
        </Text>
      </Stack>

      {loadError && (
        <Alert variant="light" color="red" icon={<IconAlertCircle size={16} />}>
          {loadError}
        </Alert>
      )}

      {!subjects && !loadError && (
        <Group gap="xs">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            Loading subject details…
          </Text>
        </Group>
      )}

      {subjects?.map((subject) => (
        <SubjectDetailCard
          key={subject.id ?? subject.subjectName}
          subject={subject}
          basicOnly={!isAdmin}
        />
      ))}

      <Group justify="flex-end">
        {canActivate ? (
          <Button
            size="md"
            color="blue"
            leftSection={<IconPlayerPlayFilled size={16} />}
            onClick={handleActivate}
            loading={activating}
          >
            Start screening
          </Button>
        ) : (
          <Text size="sm" c="dimmed" data-testid="pending-activation-hint">
            Ask your admin or project manager to start this report.
          </Text>
        )}
      </Group>
    </Stack>
  );
}
